import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';
import { MembershipPlanForm } from '@/components/organisms/pricebook/MembershipPlanForm';
import { membershipPlanKeys } from '@/lib/queryKeys';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';
import { Skeleton } from '@/components/atoms/skeleton';

export const Route = createFileRoute('/_dashboard/dashboard/membership-plans/$id/edit')({
  component: MembershipPlanEditPage,
});

function MembershipPlanEditPage() {
  const { id } = Route.useParams();
  const router = useRouter();

  const { data, isLoading } = useApiQuery<{ data: MembershipPlan } | MembershipPlan>({
    queryKey: membershipPlanKeys.detail(id),
    queryFn: () =>
      apiGet<{ data: MembershipPlan } | MembershipPlan>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlan(id),
      ),
  });

  const plan: MembershipPlan | undefined =
    data && 'data' in data ? (data as { data: MembershipPlan }).data : (data as MembershipPlan);

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!plan) {
    return <div className="p-4 text-sm text-muted-foreground">Membership plan not found.</div>;
  }

  function handleSuccess(updated: MembershipPlan) {
    // @ts-expect-error — search required by TanStack Router v1.168
    void router.navigate({
      to: '/dashboard/membership-plans/$id',
      params: { id: updated.id ?? id },
    });
  }

  function handleCancel() {
    // @ts-expect-error — search required by TanStack Router v1.168
    void router.navigate({
      to: '/dashboard/membership-plans/$id',
      params: { id },
    });
  }

  return (
    <div className="h-full flex flex-col">
      <MembershipPlanForm editing={plan} onSuccess={handleSuccess} onCancel={handleCancel} />
    </div>
  );
}
